"use client";

import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import useConfigEnv from "../../../zustand";
import RefundDetail from "./RefundDetail";

type Props = {
  id: number;
  handleOutsideClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleCloseDetailPopup: () => void;
  // Navigation props
  navigationStack?: Array<{ id: number; type: "payment" | "refund"; name: string; isAccountMove?: boolean }>;
  onBack?: () => void;
  onNavigate?: (id: number, type: "payment" | "refund", name: string) => void;
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} - ${hours}:${minutes}:${seconds}`;
};

const addHours = (dateString: any, hours: number): string => {
  const date = new Date(dateString);
  date.setHours(date.getHours() + hours);
  return date.toISOString();
};

const PaymentDetail = (props: Props) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { id, handleOutsideClick, handleCloseDetailPopup, navigationStack, onBack, onNavigate } = props;

  const [navStack, setNavStack] = useState<Array<{ id: number; type: "payment" | "refund"; name: string; isAccountMove?: boolean }>>(
    navigationStack || []
  );
  const [activeId, setActiveId] = useState<number>(id);
  const [activeType, setActiveType] = useState<"payment" | "refund">("payment");
  const [isAccountMoveDetail, setIsAccountMoveDetail] = useState<boolean>(false);
  const isNavigating = useRef(false);

  // Keep internal state updated when the wrapper changes id
  useEffect(() => {
    setActiveId(id);
    setActiveType("payment");
    setIsAccountMoveDetail(false);
    setNavStack(navigationStack || []);
    isNavigating.current = false;
  }, [id, navigationStack]);

  const [paymentDetail, setPaymentDetail] = useState<any>();
  const [showUsedCreditDetails, setShowUsedCreditDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleNavigateTarget = async (targetItem: any) => {
    if (isNavigating.current || loading) return;
    isNavigating.current = true;
    setLoading(true);
    try {
      const isRefund = targetItem.state === "Refunded";
      const modelToSearch = isRefund ? "refund.payment" : "account.move";
      const response = await fetch(`${env?.apiCall}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: modelToSearch,
          method: "web_search_read",
          ids: [],
          kwargs: {
            specification: { id: {} },
            domain: [["name", "=", targetItem.target]],
            limit: 1,
          },
        }),
      });

      if (response.status === 200) {
        const res = await response.json();
        const records = res?.records || [];
        if (records.length > 0) {
          const targetId = records[0].id;
          if (targetId === activeId && activeType === (isRefund ? "refund" : "payment")) {
            setLoading(false);
            isNavigating.current = false;
            return;
          }
          const currentName = paymentDetail?.name || "";
          setNavStack((prev) => {
            if (prev.length > 0 && prev[prev.length - 1].id === activeId) {
              return prev;
            }
            return [...prev, { id: activeId, type: activeType, name: currentName, isAccountMove: isAccountMoveDetail }];
          });
          setActiveId(targetId);
          setActiveType(isRefund ? "refund" : "payment");
          setIsAccountMoveDetail(isRefund ? false : true);
        } else {
          console.error("Target receipt not found in database:", targetItem.target);
        }
      } else {
        console.error("Error searching target receipt:", response.status);
      }
    } catch (error) {
      console.error("Error navigating to target:", error);
    } finally {
      setLoading(false);
      isNavigating.current = false;
    }
  };

  const handleGoBack = () => {
    if (isNavigating.current || loading || navStack.length === 0) return;
    isNavigating.current = true;
    const previous = navStack[navStack.length - 1];
    setNavStack((prev) => prev.slice(0, -1));
    setActiveId(previous.id);
    setActiveType(previous.type);
    if (previous.isAccountMove !== undefined) {
      setIsAccountMoveDetail(previous.isAccountMove);
    }
  };

  function compareDates(str1: any, str2: any) {
    return str1 === str2;
  }
  const totalOwe = (str1: number, str2: number) => {
    return str1 - str2;
  };
  function isDateBeforeNow(dateTimeStr: string) {
    try {
      const [datePart, timePart] = dateTimeStr.split(/ - | /);

      const [day, month, year] = datePart
        .split("-")
        .map((num) => parseInt(num));

      const [hours, minutes, seconds] = timePart
        .split(":")
        .map((num) => parseInt(num));

      const inputDate = new Date(year, month - 1, day, hours, minutes, seconds);
      const now = new Date();

      const result = inputDate.getTime() < now.getTime();
      return result;
    } catch (error) {
      console.error("Error parsing date:", error);
      return false;
    }
  }
  const { env }: any = useConfigEnv();

  useEffect(() => {
    if (activeType === "refund") {
      isNavigating.current = false;
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${env?.apiCall}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "account.move",
            method: "web_read",
            ids: [activeId],
            with_context: {
              lang: language == "vi" ? "vi_VN" : "en_US",
              tz: "Asia/Saigon",
            },
            kwargs: {
              specification: {
                name: {},
                amount_total: {},
                amount_residual: {},
                total_credit_notes: {},
                net_total_after_credit: {},
                total_credit_notes_residual: {},
                reversal_move_id: {
                  fields: { id: {}, amount_total: {} },
                },
                invoice_payments_widget: {},
                invoice_date_due: {},
                create_date: {},
                invoice_narration: {},
                paid_amount: {},
                narration: {},
                balance_applied: {},
                list_balance_applied: {},
                invoice_payment_total_amount: {},
                fund_usage_json: {},
                partner_id: {
                  fields: {
                    id: {},
                    display_name: {},
                    total_amount_outstanding: {},
                  },
                },
                tuition_program_id: {
                  fields: {
                    display_name: {},
                    due_date: {},
                  },
                },
                payment_state: {},
                invoice_stage: {},
                state: {},
                move_type: {},
                payment_account_move_transaction_widget: {},
              },
            },
          }),
        });
        if (response.status === 200) {
          const res = await response.json();
          // console.log("🚀 ~ fetchData ~ res:", res);
          const data = res[0];
          // console.log("🚀 ~ data------------", data);
          let fundUsage = [];
          try {
            if (data?.fund_usage_json) {
              fundUsage = typeof data.fund_usage_json === "string"
                ? JSON.parse(data.fund_usage_json)
                : data.fund_usage_json;
            }
          } catch (e) {
            console.error("Error parsing fund_usage_json:", e);
          }

          setPaymentDetail({
            id: data?.id,
            name: data?.name,
            studentName: data?.partner_id?.display_name,
            tuitionPeriod: data?.tuition_program_id?.display_name,
            createDate: formatDateTime(addHours(data?.create_date || false, 7)),
            dueDate: formatDateTime(
              addHours(data?.invoice_date_due || false, 7)
            ),
            move_type: data?.invoice_payments_widget?.content?.[0]?.move_type,
            paymentMethod:
              data?.invoice_payments_widget?.content?.[0]?.journal_name ||
              false,
            totalAmount: data?.amount_total,
            amount_residual: data?.amount_residual,
            paid_amount: data?.invoice_payment_total_amount,
            narration: data?.narration ? data?.narration : t("none"),
            paymentState: data?.invoice_stage || data?.payment_state,
            programDueDate: formatDateTime(
              addHours(data?.tuition_program_id?.due_date || false, 7)
            ),
            outStanding: data?.partner_id?.total_amount_outstanding,
            total_credit_notes: data?.total_credit_notes,
            net_total_after_credit: data?.net_total_after_credit,
            total_credit_notes_residual: data?.total_credit_notes_residual,
            balance_applied: data?.balance_applied,
            invoice_narration: data?.invoice_narration,
            invoice_payment_total_amount: data?.invoice_payment_total_amount,
            real_paid_amount: data?.paid_amount,
            payment_account_move_transaction_widget: data?.payment_account_move_transaction_widget,
            fundUsage: fundUsage,
          });
        } else {
          console.error("Error fetching data:", response.status);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
        isNavigating.current = false;
      }
    };

    fetchData();
  }, [activeId, activeType, env?.apiCall, language, t]);

  const totalBalance =
    (paymentDetail?.invoice_payment_total_amount || 0) -
    (paymentDetail?.real_paid_amount || 0) +
    (paymentDetail?.total_credit_notes || 0);

  const balanceResidual =
    paymentDetail?.total_credit_notes ||
      paymentDetail?.payment_account_move_transaction_widget?.some(
        (item: any) => item?.move_type === "entry"
      )
      ? (paymentDetail?.total_credit_notes_residual || 0) +
      (paymentDetail?.payment_account_move_transaction_widget
        ?.filter((item: any) => item?.move_type === "entry")
        ?.reduce((acc: any, curr: any) => acc + (curr?.unused_amount || 0), 0) || 0)
      : 0;

  const usedCredits = Array.isArray(paymentDetail?.fundUsage)
    ? paymentDetail.fundUsage.filter((item: any) => item.target && item.target !== "-" && item.target !== "Other")
    : [];

  const totalUsedCredit = usedCredits.reduce((sum: number, item: any) => sum + (Number(item.amount) || 0), 0);

  if (activeType === "refund") {
    return (
      <RefundDetail
        id={activeId}
        handleOutsideClick={handleOutsideClick}
        handleCloseDetailPopup={handleCloseDetailPopup}
        navigationStack={navStack}
        onBack={handleGoBack}
        isAccountMove={isAccountMoveDetail}
      />
    );
  }

  return (
    <>
      <div
        className="fixed z-[999999999] inset-0 flex items-center justify-center bg-black bg-opacity-50 px-2"
        onClick={handleOutsideClick}
      >
        <div className="flex flex-col items-center gap-6 rounded-2xl h-[90vh] overflow-scroll bg-white md:p-6 p-[32px] min-[320px]:p-[6px] sm max-w-2xl w-full shadow-lg relative animate-fade-in">
          <button
            onClick={handleCloseDetailPopup}
            className="absolute top-4 right-5 text-[#A4A4A4] hover:text-gray-600 text-[22px] z-10"
          >
            &times;
          </button>

          {navStack && navStack.length > 0 && (
            <div className="w-full flex justify-start -mt-2 px-1">
              <span
                onClick={handleGoBack}
                className="text-[#20B73B] font-medium cursor-pointer hover:underline text-xs md:text-sm flex items-center gap-1 border-b border-dashed border-[#20B73B]"
              >
                &lt; {navStack[navStack.length - 1].type === "refund"
                  ? t("back_to_refund_receipt", { code: navStack[navStack.length - 1].name })
                  : t("back_to_payment_receipt", { code: navStack[navStack.length - 1].name })}
              </span>
            </div>
          )}

          <h2 className="text-xl text-[#2D2D2D] font-bold text-center w-full mt-2">
            {t("payment_receipt_details", { code: paymentDetail?.name || "" })}
          </h2>

          {loading ? (
            <div className="flex justify-center items-center py-20 w-full">
              <div className="w-[48px] h-[48px] animate-spin">
                <img
                  src="/images/payment/loading.png"
                  alt="loading icon"
                  width={48}
                  height={48}
                />
              </div>
            </div>
          ) : paymentDetail ? (
            <div className="w-full flex flex-col gap-6">
              <div className="border border-[#EBEBEB] rounded-lg md:p-6 p-[16px] min-[320px]:p-[6px] w-full">
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("student")}
                    </span>
                    <span className="font-semibold text-[#20B73B] border border-[#20B73B] px-2 py-1 rounded-[8px] text-[12px] md:text-[14px]">
                      {paymentDetail.studentName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("payment_period")}
                    </span>
                    <span className="text-[12px] md:text-[14px]">
                      {paymentDetail.tuitionPeriod}
                    </span>
                  </div>
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("receipt_creation_time")}
                    </span>
                    <div className="flex items-center border border-[#D8D8D8] px-2 py-1 rounded-2xl">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-4 h-4 mr-2 text-[#666666]"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                        />
                      </svg>
                      <span className="text-[#666666] text-[12px] md:text-[14px]">
                        {paymentDetail?.createDate}
                      </span>
                    </div>
                  </div>
                  {compareDates(
                    paymentDetail?.dueDate,
                    paymentDetail?.programDueDate
                  ) ? (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("end_time")}
                      </span>
                      <div className="flex text-[12px] md:text-[14px] items-center border border-[#D8D8D8] px-2 py-1 rounded-2xl">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="w-4 h-4 mr-2 text-[#666666]"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        <span className="text-[#666666] text-[12px] md:text-[14px]">
                          {paymentDetail?.dueDate}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("end_time")}
                      </span>
                      <div className="flex text-[12px] md:text-[14px] items-center border border-[#D8D8D8] px-2 py-1 rounded-2xl">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="w-4 h-4 mr-2 text-[#666666]"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        <span className="text-[#666666] text-[12px] md:text-[14px]">
                          {paymentDetail?.programDueDate}
                        </span>
                      </div>
                    </div>
                  )}

                  {compareDates(
                    paymentDetail?.dueDate,
                    paymentDetail?.programDueDate
                  ) === false && (
                      <div className="flex justify-between items-center w-full">
                        <span className="text-gray-500 text-[12px] md:text-[14px]">
                          {t("extended_until")}
                        </span>

                        <div className="flex text-[12px] md:text-[14px] items-center border border-[#D8D8D8] px-2 py-1 rounded-2xl">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="w-4 h-4 mr-2 text-[#666666]"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                            />
                          </svg>
                          <span className="text-[#666666] text-[12px] md:text-[14px]">
                            {paymentDetail?.dueDate}
                          </span>
                        </div>
                      </div>
                    )}
                  <div className="flex justify-between items-center w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("invoice_status")}
                    </span>
                    {paymentDetail?.paymentState === "cancelled" ? (
                      <div className="flex items-center border border-[#DEE2E6] bg-[#DEE2E6] px-2 py-1 rounded-2xl">
                        <span
                          className="block w-2.5 h-2.5 rounded-full mr-2"
                          style={{
                            backgroundColor: "#8b8d8e",
                            border: "2px solid transparent",
                          }}
                        ></span>
                        <span
                          className="text-[12px] md:text-[14px]"
                          style={{
                            fontFamily: "Inter Display",
                            fontWeight: 500,
                            lineHeight: "16px",
                            textAlign: "center",
                            color: "#343A40",
                            display: "block",
                          }}
                        >
                          {t("canceled")}
                        </span>
                      </div>
                    ) : paymentDetail?.paymentState === "paid" ||
                      paymentDetail?.paymentState === "reversed" ? (
                      <div className="flex items-center border border-[#2EB5531A] bg-[#2EB5531A] px-2 py-1 rounded-2xl">
                        <span
                          className="block w-2.5 h-2.5 rounded-full mr-2"
                          style={{
                            backgroundColor: "#169C3B",
                            border: "2px solid transparent",
                          }}
                        ></span>
                        <span
                          className="text-[12px] md:text-[14px]"
                          style={{
                            fontFamily: "Inter Display",
                            fontWeight: 500,
                            lineHeight: "16px",
                            textAlign: "center",
                            color: "#169C3B",
                            display: "block",
                          }}
                        >
                          {t("paid")}
                        </span>
                      </div>
                    ) : paymentDetail?.paymentState !== "paid" &&
                      paymentDetail?.paymentState !== "cancelled" &&
                      isDateBeforeNow(paymentDetail?.dueDate) ? (
                      <div className="flex items-center border border-[#ff43191a] bg-[#ff43191a] px-2 py-1 rounded-2xl">
                        <span
                          className="block w-2.5 h-2.5 rounded-full mr-2"
                          style={{
                            backgroundColor: "#f5222d",
                            border: "2px solid transparent",
                          }}
                        ></span>
                        <span
                          className="text-[12px] md:text-[14px]"
                          style={{
                            fontFamily: "Inter Display",
                            fontWeight: 500,
                            lineHeight: "16px",
                            textAlign: "center",
                            color: "#f5222d",
                            display: "block",
                          }}
                        >
                          {t("due_date1")}
                        </span>
                      </div>
                    ) : paymentDetail?.paymentState === "partial" ? (
                      <div className="flex items-center border border-[#FFE0191A] bg-[#FFE0191A] px-2 py-1 rounded-2xl">
                        <span
                          className="block w-2.5 h-2.5 rounded-full mr-2"
                          style={{
                            backgroundColor: "#BAA416",
                            border: "2px solid transparent",
                          }}
                        ></span>
                        <span
                          className="text-[12px] md:text-[14px]"
                          style={{
                            fontFamily: "Inter Display",
                            fontWeight: 500,
                            lineHeight: "16px",
                            textAlign: "center",
                            color: "#BAA416",
                            display: "block",
                          }}
                        >
                          {t("partial_payment")}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center border border-[rgba(5,152,237,0.12)] bg-[rgba(5,152,237,0.12)] px-2 py-1 rounded-2xl">
                        <span
                          className="block w-2.5 h-2.5 rounded-full mr-2"
                          style={{
                            backgroundColor: "rgb(5,152,237)",
                            border: "2px solid transparent",
                          }}
                        ></span>
                        <span
                          className="text-[12px] md:text-[14px]"
                          style={{
                            fontFamily: "Inter Display",
                            fontWeight: 500,
                            lineHeight: "16px",
                            textAlign: "center",
                            color: "rgb(5,152,237)",
                            display: "block",
                          }}
                        >
                          {t("not_paid")}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-start w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("receipt_description")}
                    </span>
                    <span className="text-[12px] md:text-[14px] w-1/2 flex justify-end">
                      <div
                        className="text-left"
                        dangerouslySetInnerHTML={{
                          __html: paymentDetail?.invoice_narration
                            ? paymentDetail?.invoice_narration
                            : t("none"),
                        }}
                      />
                    </span>
                  </div>
                  <div className="flex justify-between items-start w-full">
                    <span className="text-gray-500 text-[12px] md:text-[14px]">
                      {t("note")}
                    </span>
                    <span className="text-[12px] md:text-[14px] w-1/2 flex justify-end">
                      <div
                        className="text-left"
                        dangerouslySetInnerHTML={{
                          __html: paymentDetail?.narration,
                        }}
                      />
                    </span>
                  </div>
                  {paymentDetail?.paymentMethod && (
                    <div className="flex justify-between items-center w-full">
                      <span className="text-gray-500 text-[12px] md:text-[14px]">
                        {t("payment_method")}
                      </span>
                      <span className="text-[#20B73B] border border-[#20B73B] px-2 py-1 rounded-[8px] text-[12px] md:text-[14px]">
                        {paymentDetail?.move_type === "out_refund"
                          ? t("apply_credit")
                          : paymentDetail?.paymentMethod}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="relative flex flex-col gap-3.5 bg-[#F5F7FA] rounded-xl p-5 w-full">
                {/* 1. Tổng tiền phiếu thu */}
                <div className="flex justify-between items-center w-full">
                  <span className="font-semibold text-[#2D2D2D] text-sm md:text-base">
                    {t("total_receipt_amount")}
                  </span>
                  <span className="font-bold text-[#2D2D2D] text-sm md:text-base">
                    {Number(paymentDetail?.net_total_after_credit || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>

                {/* 2. Đã đóng */}
                <div className="flex justify-between items-center w-full">
                  <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1.5">
                    {t("paid_amount")}
                    <span className="relative group inline-flex items-center cursor-pointer">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12C2.6862 12 0 9.3138 0 6C0 2.6862 2.6862 0 6 0C9.3138 0 12 2.6862 12 6C12 9.3138 9.3138 12 6 12ZM6 10.8C7.27304 10.8 8.49394 10.2943 9.39411 9.39411C10.2943 8.49394 10.8 7.27304 10.8 6C10.8 4.72696 10.2943 3.50606 9.39411 2.60589C8.49394 1.70571 7.27304 1.2 6 1.2C4.72696 1.2 3.50606 1.70571 2.60589 2.60589C1.70571 3.50606 1.2 4.72696 1.2 6C1.2 7.27304 1.70571 8.49394 2.60589 9.39411C3.50606 10.2943 4.72696 10.8 6 10.8V10.8ZM5.4 3H6.6V4.2H5.4V3ZM5.4 5.4H6.6V9H5.4V5.4Z" fill="#99A0AE" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1.5 hidden group-hover:block bg-gray-800 text-white text-[10px] md:text-xs px-2.5 py-1.5 rounded-lg shadow-lg w-56 whitespace-normal text-left leading-normal z-50">
                        {t("paid_amount_tooltip")}
                      </div>
                    </span>
                  </span>
                  <span className="font-semibold text-[#2D2D2D] text-xs md:text-sm">
                    {Number(paymentDetail?.paid_amount || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>

                {/* 3. Số dư đã sử dụng (Cho phiếu thu khác) */}
                <div className="flex justify-between items-center w-full">
                  <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1.5">
                    {t("used_credit_other_receipts")}
                    {usedCredits.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUsedCreditDetails(!showUsedCreditDetails);
                        }}
                        className="p-0.5 rounded hover:bg-gray-200 transition-colors focus:outline-none"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2.5}
                          stroke="currentColor"
                          className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${showUsedCreditDetails ? "rotate-180" : ""}`}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                      </button>
                    )}
                  </span>
                  <span className="font-bold text-[#FF4D4F] text-xs md:text-sm">
                    {Number(totalUsedCredit).toLocaleString("vi-VN")} VND
                  </span>
                </div>

                {/* Chi tiết số dư đã sử dụng (Dropdown) */}
                {showUsedCreditDetails && usedCredits.length > 0 && (
                  <div className="bg-white rounded-lg p-4 border border-[#EBEBEB] w-full mt-1 flex flex-col gap-2 shadow-sm text-xs md:text-sm transition-all duration-200">
                    <div className="font-bold text-[#2D2D2D] mb-1 text-left">
                      {t("used_credit_details")}:
                    </div>
                    {usedCredits.map((item: any, index: number) => {
                      const isRefund = item.state === "Refunded";
                      return (
                        <div key={index} className="flex justify-between items-center w-full text-xs md:text-sm">
                          <span className="text-[#666666] flex items-center">
                            {isRefund ? t("refund_receipt") : t("payment_receipt")}
                            : <span
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateTarget(item);
                              }}
                              className="text-blue-500 underline ml-1 font-medium cursor-pointer"
                            >
                              {item.target}
                            </span>
                            <span className="relative group inline-flex items-center ml-1 cursor-pointer">
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 12C2.6862 12 0 9.3138 0 6C0 2.6862 2.6862 0 6 0C9.3138 0 12 2.6862 12 6C12 9.3138 9.3138 12 6 12ZM6 10.8C7.27304 10.8 8.49394 10.2943 9.39411 9.39411C10.2943 8.49394 10.8 7.27304 10.8 6C10.8 4.72696 10.2943 3.50606 9.39411 2.60589C8.49394 1.70571 7.27304 1.2 6 1.2C4.72696 1.2 3.50606 1.70571 2.60589 2.60589C1.70571 3.50606 1.2 4.72696 1.2 6C1.2 7.27304 1.70571 8.49394 2.60589 9.39411C3.50606 10.2943 4.72696 10.8 6 10.8V10.8ZM5.4 3H6.6V4.2H5.4V3ZM5.4 5.4H6.6V9H5.4V5.4Z" fill="#99A0AE" />
                              </svg>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1.5 hidden group-hover:block bg-gray-800 text-white text-[10px] md:text-xs px-2.5 py-1.5 rounded-lg shadow-lg w-56 whitespace-normal text-left leading-normal z-50">
                                {/* <p className="font-bold mb-0.5">
                                  {isRefund ? t("refund_receipt") : t("payment_receipt")}
                                </p> */}
                                <p className="opacity-90">
                                  {isRefund ? t("refund_receipt_tooltip") : t("payment_receipt_tooltip")}
                                </p>
                              </div>
                            </span>
                          </span>
                          <span className="font-semibold text-[#2D2D2D]">
                            {Number(item.amount || 0).toLocaleString("vi-VN")} VND
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 4. Số dư áp dụng */}
                <div className="flex justify-between items-center w-full">
                  <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1.5">
                    {t("applied_credit")}
                    <span className="relative group inline-flex items-center cursor-pointer">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12C2.6862 12 0 9.3138 0 6C0 2.6862 2.6862 0 6 0C9.3138 0 12 2.6862 12 6C12 9.3138 9.3138 12 6 12ZM6 10.8C7.27304 10.8 8.49394 10.2943 9.39411 9.39411C10.2943 8.49394 10.8 7.27304 10.8 6C10.8 4.72696 10.2943 3.50606 9.39411 2.60589C8.49394 1.70571 7.27304 1.2 6 1.2C4.72696 1.2 3.50606 1.70571 2.60589 2.60589C1.70571 3.50606 1.2 4.72696 1.2 6C1.2 7.27304 1.70571 8.49394 2.60589 9.39411C3.50606 10.2943 4.72696 10.8 6 10.8V10.8ZM5.4 3H6.6V4.2H5.4V3ZM5.4 5.4H6.6V9H5.4V5.4Z" fill="#99A0AE" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1.5 hidden group-hover:block bg-gray-800 text-white text-[10px] md:text-xs px-2.5 py-1.5 rounded-lg shadow-lg w-56 whitespace-normal text-left leading-normal z-50">
                        {t("applied_credit_tooltip")}
                      </div>
                    </span>
                  </span>
                  <span className="font-bold text-[#20B73B] text-xs md:text-sm">
                    {Number(paymentDetail?.balance_applied || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>

                {/* Đường gạch đứt */}
                <div className="w-full border-t border-dashed border-[#EBEBEB] my-1"></div>

                {/* 5. Số tiền cần thanh toán */}
                <div className="flex justify-between items-center w-full pt-1">
                  <span className="font-bold text-[#2D2D2D] text-sm md:text-base flex items-center gap-1.5">
                    {t("amount_due")}
                    <span className="relative group inline-flex items-center cursor-pointer font-normal">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 transition-colors">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1.5 hidden group-hover:block bg-gray-800 text-white text-[10px] md:text-xs px-2.5 py-1.5 rounded-lg shadow-lg w-64 whitespace-pre-line text-left leading-normal z-50">
                        {t("amount_due_tooltip")}
                      </div>
                    </span>
                  </span>
                  <span className="font-bold text-[#2D2D2D] text-base md:text-lg">
                    {Number(paymentDetail?.amount_residual || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>
              </div>

              {/* 6. Hai block số dư song song ở dưới cùng */}
              <div className="grid grid-cols-2 gap-4 w-full mt-2">
                <div className="border border-[#EBEBEB] rounded-xl p-4 bg-white shadow-sm flex flex-col justify-between text-left">
                  <span className="text-gray-500 text-[11px] md:text-[13px] font-medium leading-tight mb-2">
                    {t("total_receipt_balance")}
                  </span>
                  <span className="font-bold text-[#2D2D2D] text-sm md:text-base">
                    {Number(totalBalance || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>
                <div className="border border-[#EBEBEB] rounded-xl p-4 bg-white shadow-sm flex flex-col justify-between text-left">
                  <span className="text-gray-500 text-[11px] md:text-[13px] font-medium leading-tight mb-2">
                    {t("remaining_receipt_balance")}
                  </span>
                  <span className="font-bold text-[#2D2D2D] text-sm md:text-base">
                    {Number(balanceResidual || 0).toLocaleString("vi-VN")} VND
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">{t("no_data")}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentDetail;
{
  /* {paymentDetail?.paymentState === "partial" || paymentDetail?.paymentState === "paid" && (
                <>
                </>
              )} */
}
